import {Artist} from '@app/web-player/artists/artist';
import {ArtistLink} from '@app/web-player/artists/artist-link';
import {Fragment, HTMLAttributeAnchorTarget} from 'react';
import {Trans} from '@ui/i18n/trans';
import clsx from 'clsx';

interface ArtistLinksProps {
  artists?: Artist[];
  className?: string;
  linkClassName?: string;
  target?: HTMLAttributeAnchorTarget;
  onLinkClick?: () => void;
  openInDialogTrigger?: boolean;
  closePlayerOverlayOnOpen?: boolean;
  onlyPrimaryArtist?: boolean;
  showMoreArtists?: boolean;
}

interface ArtistWithCreditMeta extends Artist {
  primary?: boolean | number | string | null;
  role?: string | null;
  credit_role?: string | null;
  pivot?: {
    primary?: boolean | number | string | null;
    role?: string | null;
  } | null;
}

function isTruthyPrimaryFlag(value: unknown): boolean {
  return value === true || value === 1 || value === '1' || value === 'true';
}

function isPrimaryArtist(artist: Artist): boolean {
  const candidate = artist as ArtistWithCreditMeta;
  return (
    isTruthyPrimaryFlag(candidate.primary) ||
    isTruthyPrimaryFlag(candidate.pivot?.primary) ||
    candidate.role === 'primary' ||
    candidate.credit_role === 'primary' ||
    candidate.pivot?.role === 'primary'
  );
}

export function getArtistsForDisplay(
  artists?: Artist[],
  onlyPrimaryArtist = false,
): Artist[] {
  if (!artists?.length) {
    return [];
  }

  const artistsWithOrder = artists.map((artist, index) => ({
    artist,
    index,
    isPrimary: isPrimaryArtist(artist),
  }));
  const hasExplicitPrimaryArtist = artistsWithOrder.some(item => item.isPrimary);
  const orderedArtists = hasExplicitPrimaryArtist
    ? artistsWithOrder
        .sort((a, b) => Number(b.isPrimary) - Number(a.isPrimary) || a.index - b.index)
        .map(item => item.artist)
    : [...artists];

  return onlyPrimaryArtist ? orderedArtists.slice(0, 1) : orderedArtists;
}

export function formatArtistNames(
  artists?: Artist[],
  onlyPrimaryArtist = false,
): string | undefined {
  const artistNames = getArtistsForDisplay(artists, onlyPrimaryArtist)
    .map(artist => artist.name)
    .filter(Boolean);

  return artistNames.length ? artistNames.join(', ') : undefined;
}

export function ArtistLinks({
  artists,
  className,
  target,
  linkClassName,
  onLinkClick,
  openInDialogTrigger = true,
  closePlayerOverlayOnOpen = false,
  onlyPrimaryArtist = false,
}: ArtistLinksProps) {
  const displayArtists = getArtistsForDisplay(artists, onlyPrimaryArtist);

  if (!displayArtists.length) {
    return (
      <div className={clsx('min-w-0 max-w-full truncate', className)}>
        <Trans message="Various artists" />
      </div>
    );
  }

  return (
    <div className={clsx('min-w-0 max-w-full truncate', className)}>
      {displayArtists.map((artist, i) => (
        <Fragment key={artist.id}>
          {i > 0 && ', '}
          <ArtistLink
            artist={artist}
            target={target}
            className={linkClassName}
            onClick={onLinkClick}
            openInDialogTrigger={openInDialogTrigger}
            closePlayerOverlayOnOpen={closePlayerOverlayOnOpen}
          />
        </Fragment>
      ))}
    </div>
  );
}
